import { useState } from 'react';
import { Label } from '@/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { BroadcastFilters } from '../types/broadcast.types';
import { SlidersHorizontal, ChevronDown, Languages, Tag, CalendarRange, Activity, FileText, ArrowUpDown, ArrowDownUp } from 'lucide-react';
import BroadcastDateRangePicker from './BroadcastDateRangePicker';
import { SearchableSelect } from '@/components/ui/searchable-select';

interface BroadcastFiltersProps {
    pages: any;
    categories: { id: number; name: string }[];
    filters: BroadcastFilters;
    onFiltersChange: (filters: BroadcastFilters) => void;
    availableLanguages?: string[];
}

export default function BroadcastFiltersComponent({ filters, onFiltersChange, pages, categories, availableLanguages = [] }: BroadcastFiltersProps) {
    const [isOpen, setIsOpen] = useState(false);

    const activeFilterCount = [
        filters.status,
        filters.page,
        filters.category,
        filters.targetLanguage,
        filters.dateFrom && filters.dateTo,
    ].filter(Boolean).length;

    return (
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="w-full flex items-center justify-between px-6 py-4 hover:bg-gray-50/50 transition-colors"
            >
                <div className="flex items-center gap-2">
                    <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-[#0f172a]/5">
                        <SlidersHorizontal className="h-4 w-4 text-[#0f172a]" />
                    </div>
                    <h3 className="font-semibold text-gray-900 text-sm">Filtros</h3>
                    {activeFilterCount > 0 && (
                        <span className="text-[11px] font-semibold bg-[#0f172a] text-white rounded-full px-2 py-0.5">
                            {activeFilterCount}
                        </span>
                    )}
                </div>
                <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            <div className={`grid transition-all duration-300 ease-in-out ${isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}>
                <div className="overflow-hidden">
                    <div className="px-6 pb-6 pt-2">
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                            {/* Periodo de criacao */}
                            <div className="space-y-2">
                                <Label className="text-xs font-medium text-gray-400 uppercase tracking-wider flex items-center gap-1">
                                    <CalendarRange className="h-3 w-3" />
                                    Período de criação
                                </Label>
                                <BroadcastDateRangePicker
                                    dateFrom={filters.dateFrom}
                                    dateTo={filters.dateTo}
                                    onChange={(from, to) =>
                                        onFiltersChange({ ...filters, dateFrom: from, dateTo: to })
                                    }
                                />
                            </div>

                            {/* Status */}
                            <div className="space-y-2">
                                <Label htmlFor="status" className="text-xs font-medium text-gray-400 uppercase tracking-wider flex items-center gap-1">
                                    <Activity className="h-3 w-3" />
                                    Status
                                </Label>
                                <Select
                                    value={filters.status || 'all'}
                                    onValueChange={(value) =>
                                        onFiltersChange({ ...filters, status: value === 'all' ? undefined : value as any })
                                    }
                                >
                                    <SelectTrigger id="status" className="h-10 rounded-xl border-gray-200 focus:border-[#7dd3fc] focus:ring-[#7dd3fc]/20">
                                        <SelectValue placeholder="Todos" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Todos</SelectItem>
                                        <SelectItem value="pending">Pendente</SelectItem>
                                        <SelectItem value="processing">Enviando</SelectItem>
                                        <SelectItem value="sent">Enviado</SelectItem>
                                        <SelectItem value="partial">Enviado parcialmente</SelectItem>
                                        <SelectItem value="failed">Falhou</SelectItem>
                                        <SelectItem value="cancelled">Cancelado</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            {/* Página */}
                            <div className="space-y-2">
                                <Label htmlFor="page" className="text-xs font-medium text-gray-400 uppercase tracking-wider flex items-center gap-1">
                                    <FileText className="h-3 w-3" />
                                    Página
                                </Label>
                                <Select
                                    value={filters.page ?? "all"}
                                    onValueChange={(value) =>
                                        onFiltersChange({ ...filters, page: value === "all" ? undefined : value })
                                    }
                                >
                                    <SelectTrigger id="page" className="h-10 rounded-xl border-gray-200 focus:border-[#7dd3fc] focus:ring-[#7dd3fc]/20">
                                        <SelectValue placeholder="Todas" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Todas</SelectItem>
                                        {pages.map((p: any) => (
                                            <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            {/* Categoria */}
                            <div className="space-y-2">
                                <Label htmlFor="category" className="text-xs font-medium text-gray-400 uppercase tracking-wider flex items-center gap-1">
                                    <Tag className="h-3 w-3" />
                                    Categoria
                                </Label>
                                <SearchableSelect
                                    value={filters.category || 'all'}
                                    onValueChange={(value) =>
                                        onFiltersChange({ ...filters, category: value === 'all' ? undefined : value })
                                    }
                                    options={[
                                        { value: 'all', label: 'Todas' },
                                        ...categories.map(c => ({ value: String(c.id), label: c.name }))
                                    ]}
                                    placeholder="Todas"
                                    searchPlaceholder="Buscar categoria..."
                                    className="h-10 rounded-xl border-gray-200 focus:border-[#7dd3fc] focus:ring-[#7dd3fc]/20"
                                />
                            </div>

                            {/* Idioma de tradução */}
                            <div className="space-y-2">
                                <Label htmlFor="targetLanguage" className="text-xs font-medium text-gray-400 uppercase tracking-wider flex items-center gap-1">
                                    <Languages className="h-3 w-3" />
                                    Idioma I.A
                                </Label>
                                <Select
                                    value={filters.targetLanguage || 'all'}
                                    onValueChange={(value) =>
                                        onFiltersChange({ ...filters, targetLanguage: value === 'all' ? undefined : value })
                                    }
                                >
                                    <SelectTrigger id="targetLanguage" className="h-10 rounded-xl border-gray-200 focus:border-[#7dd3fc] focus:ring-[#7dd3fc]/20">
                                        <SelectValue placeholder="Todos" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Todos</SelectItem>
                                        <SelectItem value="__none__">Sem tradução</SelectItem>
                                        {availableLanguages.map((lang) => (
                                            <SelectItem key={lang} value={lang}>{lang}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            {/* Ordenar por */}
                            <div className="space-y-2">
                                <Label htmlFor="sortBy" className="text-xs font-medium text-gray-400 uppercase tracking-wider flex items-center gap-1">
                                    <ArrowUpDown className="h-3 w-3" />
                                    Ordenar por
                                </Label>
                                <Select
                                    value={filters.sortBy || 'created_at'}
                                    onValueChange={(value) =>
                                        onFiltersChange({ ...filters, sortBy: value as any })
                                    }
                                >
                                    <SelectTrigger id="sortBy" className="h-10 rounded-xl border-gray-200 focus:border-[#7dd3fc] focus:ring-[#7dd3fc]/20">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="created_at">Data de Criação</SelectItem>
                                        <SelectItem value="sent_count">Quantidade de Envios</SelectItem>
                                        <SelectItem value="success_rate">Taxa de Sucesso</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            {/* Ordem */}
                            <div className="space-y-2">
                                <Label htmlFor="sortOrder" className="text-xs font-medium text-gray-400 uppercase tracking-wider flex items-center gap-1">
                                    <ArrowDownUp className="h-3 w-3" />
                                    Ordem
                                </Label>
                                <Select
                                    value={filters.sortOrder || 'desc'}
                                    onValueChange={(value) =>
                                        onFiltersChange({ ...filters, sortOrder: value as any })
                                    }
                                >
                                    <SelectTrigger id="sortOrder" className="h-10 rounded-xl border-gray-200 focus:border-[#7dd3fc] focus:ring-[#7dd3fc]/20">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="desc">Decrescente</SelectItem>
                                        <SelectItem value="asc">Crescente</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        {/* Clear filters */}
                        {activeFilterCount > 0 && (
                            <div className="mt-4 pt-3 border-t border-gray-50">
                                <button
                                    onClick={() => onFiltersChange({ sortBy: 'created_at', sortOrder: 'desc' })}
                                    className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
                                >
                                    Limpar filtros ativos
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}